import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { forgotPasswordApi, toAuthResult } from "@/portals/patient/api/patientAuthApi";
import { saveAuthFlowState } from "./authFlowState";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { AuthTextField } from "@/shared/components/AuthField";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { Button } from "@/shared/components/button";
import { validateFields } from "@/shared/lib/validators";
import { AuthHeading } from "@/shared/components/AuthHeading";

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const clearError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const fieldErrors = validateFields(
      { email },
      {
        email: [
          { required: true, message: "Email is required" },
          { type: "email", message: "Enter a valid email address" },
        ],
      },
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setLoading(true);
    const result = toAuthResult(await forgotPasswordApi(email));
    setLoading(false);

    if (result.success) {
      saveAuthFlowState({ email });
      toastSuccess("OTP has been sent successfully. Please check your email.");
      navigate("/patient/auth/verify-reset-otp", { state: { email } });
    } else {
      toastError(result.error ?? "Unable to send code.");
    }
  };

  return (
    <div className="w-full">
      <AuthHeading
        title={<>Forgot your <span className="font-extrabold tracking-tight">password?</span></>}
        subtitle="Enter your email and we'll send you a code to reset it."
        className="mb-8"
      />

      <form onSubmit={handleSubmit}>
        <AuthTextField
          required
          label="Email"
          placeholder="Enter your email"
          value={email}
          onChange={(v) => { setEmail(v); clearError("email"); }}
          error={errors.email}
          disabled={loading}
          className="mb-6"
        />

        <AuthSubmitButton loading={loading}>Send OTP</AuthSubmitButton>
      </form>

      <p className="!mt-5 text-center text-sm text-[#9CA3AF]">
        Remembered your password?{" "}
        <Button
          variant="link"
          disabled={loading}
          onClick={() => navigate("/patient/auth/signin")}
          className="!h-auto !p-0 !text-sm !font-semibold !text-[#E4002B] hover:!text-[#E4002B] hover:!underline disabled:!no-underline"
        >
          Sign in
        </Button>
      </p>
    </div>
  );
}
